import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDemo } from '../contexts/DemoContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatErrorMessage } from '../utils/errorHandling';
import { productService } from '../services/firebaseService';

export default function BookDemo() {
  const { register, handleSubmit, watch, reset, formState: { errors }, setError, clearErrors, setValue } = useForm();
  const { bookDemo, loading, hosts, hostsLoading, conflictResolutionResult, clearConflictResolution } = useDemo();
  const { currentAgent } = useAuth();
  const { isReferrer, isHost, isBooking } = usePermissions();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [selectedHost, setSelectedHost] = useState('');
  const [hostingGiftProducts, setHostingGiftProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const demoLocation = watch('demoLocation');
  const isHosted = watch('isHosted');
  const trainingType = watch('trainingType');

  // Code validation function using existing Cloud Function with reverse logic
  const checkCodeValidity = async (code) => {
    if (!code) return { checking: false, valid: null };

    try {
      const response = await fetch('https://europe-west3-plsss-project.cloudfunctions.net/checkAgentIdAvailability', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId: code })
      });

      const result = await response.json();
      // Reverse logic: available === false means code exists (valid)
      return { checking: false, valid: !result.available };
    } catch (err) {
      console.error('Error checking code validity:', err);
      return { checking: false, valid: null };
    }
  };

  // Load hosting gift products on mount
  useEffect(() => {
    const loadHostingGiftProducts = async () => {
      try {
        setLoadingProducts(true);
        const allProducts = await productService.getProducts();
        // Filter products where isHostingGift is true
        // const giftProducts = Object.values(allProducts).filter(
        //   product => product.isHostingGift === true
        // );
        
        // Filter products where Categories field contains "HstGft"
        const giftProducts = Object.values(allProducts).filter(
          product => product.Categories && product.Categories.includes('HstGft')
        );
        
        setHostingGiftProducts(giftProducts);
      } catch (error) {
        console.error('Error loading hosting gift products:', error);
      } finally {
        setLoadingProducts(false);
      }
    };
    
    loadHostingGiftProducts();
  }, []);

  // Listen for conflict resolution success from context
  useEffect(() => {
    if (conflictResolutionResult?.success) {
      console.log('Conflict resolution completed successfully, resetting form');
      
      // Reset the form
      const resetValues = {
        demoLocation: '',
        isHosted: 'NO',
        customerName: '',
        customerMobile: '',
        hostName: '',
        hostPhone: '',
        demoDate: '',
        timeSlot: '',
        notes: '',
        trainingType: '',
        hostingGift: '',
        phonePrefix: ''
      };
      
      reset(resetValues);
      setSelectedHost('');
      setSubmitError(null);
      
      // Show success message
      setSuccess({
        message: `Demo booked successfully with host ${conflictResolutionResult.hostName} (conflict resolved)`,
        shouldCloseForm: true,
        conflictResolved: true,
        hostName: conflictResolutionResult.hostName,
        demoId: conflictResolutionResult.demoId
      });
      
      // Clear the conflict resolution result from context
      clearConflictResolution();
    }
  }, [conflictResolutionResult, reset, clearConflictResolution]);

  const onSubmit = async (data) => {
    console.log('1. Form submission started');
    console.log('Current state:', {
      isHost,
      isBooking,
      teamLeader: currentAgent?.apps?.agents?.teamLeader,
      currentAgent: currentAgent
    });
    setSubmitError(null);
    clearErrors();
    
    try {
      // For hosts and booking agents, auto-populate host details and set associated agent
      if (isHost || isBooking) {
        data.hostName = currentAgent.name;
        data.hostPhone = currentAgent.phoneno || currentAgent.phone || currentAgent.mobile;
        data.hostAgentId = currentAgent.agent_id;
        
        // Use teamLeader field directly from currentAgent context
        if (currentAgent?.apps?.agents?.teamLeader) {
          data.associatedAgentCode = currentAgent.apps.agents.teamLeader;
          console.log('Using teamLeader from context:', data.associatedAgentCode);
        } else {
          console.warn('Host/Booking agent has no teamLeader field configured');
        }
        
        // For booking agents, add BkpCode field
        if (isBooking) {
          data.bkpCode = currentAgent.agentCode || currentAgent.agent_id;
        }
        
        data.isHosted = 'YES'; // Always hosted for hosts and booking agents
        
        // Set default training type since the field is hidden for these roles
        if (!data.trainingType) {
          data.trainingType = 'Other';
        }
      }
      
      // If hosting gift is selected, add product details to the data
      if (data.hostingGift) {
        const selectedProduct = hostingGiftProducts.find(p => p.id === data.hostingGift);
        if (selectedProduct) {
          data.hostingGiftCode = selectedProduct.code;
          data.hostingGiftName = selectedProduct.name;
        }
      }
      
      console.log('2. Calling bookDemo with data:', { 
        customerName: data.customerName,
        hostPhone: data.hostPhone,
        demoLocation: data.demoLocation,
        hostingGift: data.hostingGift,
        hostingGiftCode: data.hostingGiftCode,
        hostingGiftName: data.hostingGiftName
      });
      
      const result = await bookDemo(data);
      
      // Handle waiting for conflict resolution - DO NOT reset form yet
      if (result.waitingForConfirmation) {
        setSubmitError({
          waitingForConfirmation: true,
          message: 'Resolving host conflict...'
        });
        return; // Exit early, form should NOT reset yet
      }
      
      // Handle ALL success cases (direct success OR conflict resolution success)
      if (result.success) {
        console.log('4. BookDemo was successful, preparing success state');
        
        // ✅ RESET FORM FOR ALL SUCCESS CASES
        const resetValues = {
          demoLocation: '',
          isHosted: 'NO',
          customerName: '',
          customerMobile: '',
          hostName: '',
          hostPhone: '',
          demoDate: '',
          timeSlot: '',
          notes: '',
          trainingType: '',
          hostingGift: '',
          phonePrefix: ''
        };
        
        // Reset the form with default values
        reset(resetValues);
        
        // Clear any selected host
        setSelectedHost('');
        
        // Show success message with conflict resolution details if applicable
        let successMessage = 'Demo booked successfully';
        if (result.conflictResolved) {
          successMessage = `Demo booked successfully with host ${result.hostName || 'the host'} (conflict resolved)`;
        } else if (result.hostName) {
          successMessage = `Demo booked successfully with host ${result.hostName}`;
        }
        
        console.log('5. Setting success state with message:', successMessage);
        setSuccess({
          message: successMessage,
          shouldCloseForm: result.shouldCloseForm !== false, // Default to true if not specified
          conflictResolved: result.conflictResolved,
          hostName: result.hostName,
          demoId: result.demoId
        });
        
        return; // Exit early for successful submission
      }
      
      // Handle specific error cases
      if (result.error === 'CUSTOMER_CLAIMED') {
        setError('customerMobile', {
          type: 'manual',
          message: formatErrorMessage(result)
        });
        setSubmitError(result);
      } else {
        setSubmitError({
          error: 'SUBMIT_ERROR',
          message: formatErrorMessage(result)
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError({
        error: 'UNKNOWN_ERROR',
        message: 'An unexpected error occurred. Please try again.'
      });
    }
  };

  // Handle success redirect
  useEffect(() => {
    if (success?.shouldCloseForm) {
      console.log('Setting up redirect timer');
      const timer = setTimeout(() => {
        console.log('Executing redirect to dashboard');
        setSuccess(null);
        navigate('/dashboard');
      }, 5000);
      
      return () => {
        console.log('Cleaning up timer');
        clearTimeout(timer);
      };
    }
  }, [success, navigate]);

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-md text-center">
          <div className="text-green-600 text-4xl mb-3">✅</div>
          <h2 className="text-lg font-bold text-green-800 mb-2">
            {success.conflictResolved 
              ? 'Demo Booked Successfully (Conflict Resolved)' 
              : 'Demo Booked Successfully!'}
          </h2>
          {success.message && (
            <p className="text-green-700 mb-3 text-sm">{success.message}</p>
          )}
          <div className="text-green-700 space-y-1 text-sm text-left bg-green-50 p-3 rounded">
            <p><strong>Demo ID:</strong> {success.demoId || 'N/A'}</p>
            <p><strong>Customer:</strong> {success.customer?.name || success.customerName || 'N/A'}</p>
            {success.hostName && success.hostName !== 'N/A' && (
              <div className="mt-2 pt-2 border-t border-green-100">
                <p><strong>Host:</strong> {success.hostName} {success.conflictResolved ? '(Conflict Resolved)' : ''}</p>
              </div>
            )}
          </div>
          
          <p className="text-xs text-green-600 mt-3">Redirecting to dashboard in 5 seconds...</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
          >
            Go to Dashboard Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 max-w-6xl mx-auto px-4 py-4 pb-32 overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-4">📅 Book Demo</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col space-y-4">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* Customer Details */}
            <div className="bg-gray-50 p-4 rounded-xl">
              <h3 className="text-base font-semibold text-gray-800 mb-3">Customer Details</h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Name *
                  </label>
                  <input
                    {...register('customerName', { required: 'Customer name is required' })}
                    className="form-input w-full"
                    placeholder="Enter customer name"
                  />
                  {errors.customerName && (
                    <p className="text-red-500 text-xs mt-1">{errors.customerName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Country *
                  </label>
                  <select
                    {...register('phonePrefix', { required: 'Customer country is required' })}
                    className="form-select w-full"
                  >
                    <option value="">Select Country</option>
                    <option value="+973">🇧🇭 Bahrain</option>
                    <option value="+966">🇸🇦 Saudi Arabia</option>
                    <option value="+971">🇦🇪 United Arab Emirates</option>
                    <option value="+63">🇵🇭 Philippines</option>
                    <option value="OTHER">🌍 Other Country</option>
                  </select>
                  {errors.phonePrefix && (
                    <p className="text-red-500 text-xs mt-1">{errors.phonePrefix.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile Number *
                  </label>
                  <input
                    {...register('customerMobile', { 
                      required: 'Mobile number is required',
                      pattern: {
                        value: /^\+[1-9]\d{1,3}\d{7,14}$/,
                        message: 'Must be a valid international number (e.g., +97333123456)'
                      },
                      minLength: {
                        value: 11,
                        message: 'Number too short (minimum 10 digits including country code)'
                      },
                      maxLength: {
                        value: 16,
                        message: 'Number too long (maximum 15 digits after +)'
                      },
                      onChange: () => {
                        if (submitError?.error === 'CUSTOMER_CLAIMED') {
                          setSubmitError(null);
                          clearErrors('customerMobile');
                        }
                      }
                    })}
                    type="tel"
                    className="form-input w-full"
                    placeholder="+97333123456"
                    aria-invalid={errors.customerMobile ? 'true' : 'false'}
                  />
                  {errors.customerMobile ? (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.customerMobile.message}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      📱 Format: +[country code][number] (e.g., +97333123456, +971501234567)
                      <br />
                      Minimum 10 digits including country code, maximum 15 digits after +
                    </p>
                  )}
                </div>

                {/* Training Type - Hidden for booking and host roles */}
                {!isHost && !isBooking && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Training Type
                    </label>
                    <select
                      {...register('trainingType')}
                      className="form-select w-full"
                    >
                      <option value="">Select Training Type</option>
                      <option value="Blast-Off 1">Blast-Off 1</option>
                      <option value="Blast-Off 2">Blast-Off 2</option>
                      <option value="Blast-Off 3">Blast-Off 3</option>
                      <option value="Blast-Off 4">Blast-Off 4</option>
                      <option value="Blast-Off 5">Blast-Off 5</option>
                      <option value="Blast-Off 6">Blast-Off 6</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.trainingType && (
                      <p className="text-red-500 text-xs mt-1">{errors.trainingType.message}</p>
                    )}
                  </div>
                )}

                {/* Training Code - Only show when Training Type is selected */}
                {!isHost && !isBooking && trainingType && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Training Code (Optional)
                    </label>
                    <div className="text-xs text-gray-500 mb-2">
                      Enter 3-letter training code (e.g., POP). Defaults to team leader code if not specified.
                    </div>
                    <input
                      {...register('trnCode', {
                        pattern: {
                          value: /^[A-Z]{3}$/,
                          message: 'Must be a valid 3-letter code (e.g., POP)'
                        }
                      })}
                      type="text"
                      className="form-input w-full uppercase"
                      placeholder="POP"
                      maxLength={3}
                    />
                    {errors.trnCode && (
                      <p className="text-red-500 text-xs mt-1">{errors.trnCode.message}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Demo Details */}
            <div className="bg-gray-50 p-4 rounded-xl">
              <h3 className="text-base font-semibold text-gray-800 mb-3">Demo Details</h3>
              
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Demo Date *
                    </label>
                    <input
                      {...register('demoDate', { required: 'Demo date is required' })}
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      className="form-input w-full"
                    />
                    {errors.demoDate && (
                      <p className="text-red-500 text-xs mt-1">{errors.demoDate.message}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time Slot *
                    </label>
                    <select
                      {...register('timeSlot', { required: 'Time slot is required' })}
                      className="form-select w-full"
                    >
                      <option value="">Select Time</option>
                      <option value="Morning">Morning (9-12)</option>
                      <option value="Afternoon">Afternoon (1-5)</option>
                      <option value="Evening">Evening (6-9)</option>
                    </select>
                    {errors.timeSlot && (
                      <p className="text-red-500 text-xs mt-1">{errors.timeSlot.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Demo Location *
                  </label>
                  <select
                    {...register('demoLocation', { required: 'Demo location is required' })}
                    className="form-select w-full"
                  >
                    <option value="">Select Location</option>
                    <option value="Bahrain Office">🏢 Bahrain Office</option>
                    <option value="Dubai Office">🏢 Dubai Office</option>
                    <option value="Virtual">💻 Virtual Demo</option>
                    <option value="Client">🏠 Client Location</option>
                  </select>
                  {errors.demoLocation && (
                    <p className="text-red-500 text-xs mt-1">{errors.demoLocation.message}</p>
                  )}
                </div>


                {/* Hosting gift section for hosts and booking agents */}
                {(isHost || isBooking) && (
                  <div className="bg-white p-3 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Introduction Gift *
                    </label>
                    <div className="text-xs text-gray-500 mb-2">
                      Select a gift for introducing this client to your associated agent
                    </div>
                    {loadingProducts ? (
                      <div className="text-sm text-gray-500">Loading gifts...</div>
                    ) : (
                      <select
                        {...register('hostingGift', {
                          required: 'Introduction gift is required for host bookings'
                        })}
                        className="form-select w-full"
                      >
                        <option value="">Select Introduction Gift</option>
                        {hostingGiftProducts.map(product => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {errors.hostingGift && (
                      <p className="text-red-500 text-xs mt-1">{errors.hostingGift.message}</p>
                    )}
                  </div>
                )}

                {/* Hosting Options - NOT available for hosts or booking agents */}
                {!isHost && !isBooking && (
                  <div className="bg-white p-3 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Demo Hosting
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center text-sm">
                        <input
                          {...register('isHosted')}
                          type="radio"
                          value="NO"
                          className="mr-2"
                          defaultChecked
                        />
                        Not Hosted
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          {...register('isHosted')}
                          type="radio"
                          value="YES"
                          className="mr-2"
                        />
                        Hosted Demo
                      </label>
                    </div>

                    {/* Host Details */}
                    {isHosted === 'YES' && (
                      <div className="mt-3 space-y-3">
                        <h4 className="text-sm font-medium text-gray-700">Host Details</h4>
                        
                        {hostsLoading ? (
                          <div className="text-sm text-gray-500">Loading hosts...</div>
                        ) : hosts.length > 0 ? (
                          <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Select Host
                            </label>
                            <select
                              className="form-select w-full text-sm mb-3"
                              value={selectedHost}
                              onChange={(e) => {
                                const hostId = e.target.value;
                                setSelectedHost(hostId);
                                
                                if (hostId === 'new') {
                                  // Clear fields for new host
                                  setValue('hostName', '');
                                  setValue('hostPhone', '');
                                } else {
                                  // Pre-fill host details
                                  const host = hosts.find(h => h.id === hostId);
                                  if (host) {
                                    setValue('hostName', host.name || '');
                                    setValue('hostPhone', host.phone || '');
                                  }
                                }
                              }}
                            >
                              <option value="">Select a host or add new...</option>
                              {hosts.map(host => (
                                <option key={host.id} value={host.id}>
                                  {host.name} - {host.phone}
                                </option>
                              ))}
                              <option value="new">+ Add New Host</option>
                            </select>
                          </div>
                        ) : (
                          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded">
                            <div className="flex">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h.01a.5.5 0 100-1H10v-3a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                  No hosts found for agent ID: <code className="bg-blue-100 px-1 rounded">{currentAgent?.agent_id || 'No agent ID'}</code>. Please add a host to continue with a hosted demo.
                                </p>
                                <div className="mt-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedHost('new');
                                      setValue('hostName', '');
                                      setValue('hostPhone', '');
                                    }}
                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <svg className="-ml-0.5 mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                    Add New Host
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Host Name *
                            </label>
                            <input
                              {...register('hostName', { 
                                required: isHosted === 'YES' ? 'Host name is required' : false 
                              })}
                              className="form-input w-full text-sm"
                              placeholder="Host name"
                            />
                            {errors.hostName && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.hostName.message}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Host Phone *
                            </label>
                            <input
                              {...register('hostPhone', {
                                required: isHosted === 'YES' ? 'Host phone is required' : false,
                                pattern: {
                                  value: /^\+[1-9]\d{1,3}\d{7,14}$/,
                                  message: 'Must be a valid international number (e.g., +97333123456)'
                                },
                                minLength: {
                                  value: 11,
                                  message: 'Number too short (minimum 10 digits including country code)'
                                },
                                maxLength: {
                                  value: 16,
                                  message: 'Number too long (maximum 15 digits after +)'
                                }
                              })}
                              type="tel"
                              className="form-input w-full text-sm"
                              placeholder="+97333123456"
                              aria-invalid={errors.hostPhone ? 'true' : 'false'}
                            />
                            {errors.hostPhone ? (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.hostPhone.message}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-500 mt-1">
                                📱 Format: +[country code][number] (e.g., +97333123456, +971501234567)
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Hosting Gift - Only shown when demo is hosted */}
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Hosting Gift {!isBooking && '*'}
                      </label>
                      <div className="text-xs text-gray-500 mb-1">
                        {isBooking
                          ? 'Optional for booking partners (commission-based arrangement)'
                          : 'Required when host details are specified'
                        }
                      </div>
                      {loadingProducts ? (
                        <div className="text-sm text-gray-500">Loading gifts...</div>
                      ) : (
                        <select
                          {...register('hostingGift', {
                            required: !isBooking && demoLocation === 'Client' && isHosted === 'YES'
                              ? 'Hosting gift is required for hosted demos'
                              : false
                          })}
                          className="form-select w-full"
                        >
                          <option value="">Select Hosting Gift</option>
                          {hostingGiftProducts.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name}
                            </option>
                          ))}
                        </select>
                      )}
                      {errors.hostingGift && (
                        <p className="text-red-500 text-xs mt-1">{errors.hostingGift.message}</p>
                      )}
                    </div>

                    {/* Booking Partner Code - Optional field - hide when Hosted Demo is selected */}
                    {isHosted !== 'YES' && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Booking Partner Code (Optional)
                        </label>
                        <div className="text-xs text-gray-500 mb-2">
                          Enter 3-letter booking partner code (e.g., POP). If specified, host details will be set to null (mutually exclusive).
                        </div>
                        <input
                          {...register('bkpCode', {
                            pattern: {
                              value: /^[A-Z]{3}$/,
                              message: 'Must be a valid 3-letter code (e.g., POP)'
                            }
                          })}
                          type="text"
                          className="form-input w-full uppercase"
                          placeholder="POP"
                          maxLength={3}
                        />
                        {errors.bkpCode && (
                          <p className="text-red-500 text-xs mt-1">{errors.bkpCode.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Add some spacing at the bottom */}
          <div className="h-8"></div>

          {/* Success/Error Messages - Fixed at bottom */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-10">
            <div className="max-w-6xl mx-auto px-2">
              <div className="flex flex-col space-y-2">
                {/* Success Message */}
                {success?.message && (
                  <div className="p-2 bg-green-50 border-l-4 border-green-400 rounded">
                    <div className="flex items-center">
                      <svg className="h-4 w-4 text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-green-700 text-xs font-medium">
                        {success.message}
                      </p>
                    </div>
                    {success.shouldCloseForm && (
                      <p className="text-green-600 text-xs mt-1 ml-6">
                        Redirecting to dashboard...
                      </p>
                    )}
                  </div>
                )}

                {/* Error Message */}
                {submitError?.error && submitError.error !== 'CUSTOMER_CLAIMED' && !submitError.waitingForConfirmation && (
                  <div className="p-2 bg-red-50 border-l-4 border-red-400 rounded">
                    <div className="flex items-center">
                      <svg className="h-4 w-4 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-red-700 text-xs font-medium">
                        {submitError.message}
                      </p>
                    </div>
                  </div>
                )}

                {/* Waiting for Conflict Resolution */}
                {submitError?.waitingForConfirmation && (
                  <div className="p-2 bg-blue-50 border-l-4 border-blue-400 rounded">
                    <div className="flex items-center">
                      <svg className="h-4 w-4 text-blue-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-blue-700 text-xs font-medium">
                        Resolving host conflict... Please check the confirmation dialog.
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Booking Agent Details - Shown at end of form */}
                {(isHost || isBooking) && currentAgent?.apps?.agents?.teamLeader && (
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <div className="text-xs text-blue-700 space-y-1">
                      <p><strong>Associated Agent Code:</strong> {currentAgent.apps.agents.teamLeader}</p>
                      <p className="text-xs mt-1">You are introducing this client to your associated agent</p>
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? <LoadingSpinner size={4} /> : 'Book Demo'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
